import { useCallback, useEffect, useState } from "react";

import { readAvatarCacheBuster, withAvatarCacheBust, writeAvatarCacheBuster } from "~services/avatar/avatarCacheBuster";
import { type AvatarEntityType } from "~services/avatar/avatarEntityTypes";
import { makeAvatarUrl } from "~services/avatar/makeAvatarUrl";

type AvatarUploadStatus = "idle" | "loading" | "error" | "success";

interface EntityAvatarUploadVariables {
  pathParams: object;
}

export interface UseEntityAvatarOptions<
  TVariables extends EntityAvatarUploadVariables = { pathParams: Record<string, string> },
> {
  baseImageUrl?: string;
  entityId: string | undefined;
  entityType: AvatarEntityType;
  onUploadSuccess?: (imageUrl: string) => void;
  pathParamKey: keyof TVariables["pathParams"] & string;
  uploadPicture: (variables: TVariables) => Promise<unknown>;
}

export interface UseEntityAvatarResult {
  imageUrl: string | undefined;
  status: AvatarUploadStatus;
  uploadImage: (file: File) => Promise<string>;
}

export function useEntityAvatar<
  TVariables extends EntityAvatarUploadVariables = { pathParams: Record<string, string> },
>({
  baseImageUrl: baseImageUrlOverride,
  entityId,
  entityType,
  onUploadSuccess,
  pathParamKey,
  uploadPicture,
}: UseEntityAvatarOptions<TVariables>): UseEntityAvatarResult {
  const [status, setStatus] = useState<AvatarUploadStatus>("idle");
  const [uploadTimestamp, setUploadTimestamp] = useState<number | undefined>();

  useEffect(() => {
    if (!entityId) {
      setUploadTimestamp(undefined);
      return;
    }

    const stored = readAvatarCacheBuster(entityType, entityId);
    setUploadTimestamp(stored ? Number(stored) : undefined);
  }, [entityId, entityType]);

  const baseImageUrl = baseImageUrlOverride ?? (entityId ? makeAvatarUrl(entityType, entityId) : undefined);
  const imageUrl =
    baseImageUrl && entityId ? withAvatarCacheBust(entityType, baseImageUrl, entityId, uploadTimestamp) : baseImageUrl;

  const uploadImage = useCallback(
    async (file: File) => {
      if (!entityId || !baseImageUrl) {
        setStatus("error");
        throw new Error(`Cannot upload image without a ${entityType} ID`);
      }

      setStatus("loading");

      const formData = new FormData();
      formData.append("FormFile", file);

      try {
        await uploadPicture({
          body: formData,
          headers: { "Content-Type": undefined },
          pathParams: { [pathParamKey]: entityId },
        } as unknown as TVariables);

        const timestamp = Date.now();
        writeAvatarCacheBuster(entityType, entityId, timestamp);
        setUploadTimestamp(timestamp);
        setStatus("success");
        const nextImageUrl = withAvatarCacheBust(entityType, baseImageUrl, entityId, timestamp);
        onUploadSuccess?.(nextImageUrl);
        return nextImageUrl;
      } catch {
        setStatus("error");
        throw new Error(`Failed to upload ${entityType} picture`);
      }
    },
    [baseImageUrl, entityId, entityType, onUploadSuccess, pathParamKey, uploadPicture],
  );

  return {
    imageUrl,
    status,
    uploadImage,
  };
}
